import React, { useState, useEffect } from 'react';
import uniqid from 'uniqid';
import classNames from 'classnames';

import Icons from 'assets/icons';

interface NavItem {
  id: string | number;
  navigationTitle?: string;
  type?: string;
  [key: string]: unknown;
}

interface ActiveItem {
  navigationTitle?: string;
  id?: string | number;
}

interface PageNavProps {
  content?: NavItem[];
  isTabletView?: boolean;
  navTitle?: string;
}

const PageNav = ({
  content = [],
  isTabletView = false,
  navTitle = 'In this article',
}: PageNavProps): React.JSX.Element | false => {
  const [activeItem, setActiveItem] = useState<ActiveItem>({});
  const [filteredContent, setFilteredContent] = useState<NavItem[]>([]);
  const [isNavBarOpen, setIsNavBarOpen] = useState<boolean>(false);

  const handleItemClick = ({
    navigationTitle,
    id,
  }: {
    navigationTitle?: string;
    id: string | number;
  }): void => {
    setActiveItem({ navigationTitle, id });

    const element = document.getElementById(`block_${id}`);
    const elPosition = element!.getBoundingClientRect();

    window.scrollTo(window.scrollX, elPosition.top - 112 + window.scrollY);

    setIsNavBarOpen(false);
  };

  useEffect(() => {
    const filteredParagraphs =
      content?.filter(
        (el) =>
          el.type === 'paragraph' &&
          el.navigationTitle &&
          !Object.is(el.navigationTitle, null)
      ) ?? [];

    setFilteredContent(filteredParagraphs);
    setActiveItem(filteredParagraphs[0] ?? {});
  }, []);

  return (
    !!filteredContent.length &&
    (!isTabletView ? (
      <div className="mobile:mb-6 xs:mb-8">
        <div className="py-2 text-navy-500 font-semibold">{navTitle}</div>
        {filteredContent.map(({ navigationTitle, id }) => {
          const activeStyles =
            activeItem.id === id && 'bg-navy-50 font-semibold';

          return (
            <button
              type="button"
              key={uniqid()}
              className={classNames(
                `w-full text-left py-2 px-3 my-0.5 cursor-pointer rounded-sm`,
                activeStyles
              )}
              onClick={() => handleItemClick({ navigationTitle, id })}
            >
              {navigationTitle}
            </button>
          );
        })}
      </div>
    ) : (
      <div className="relative mobile:mb-6 xs:mb-8">
        <button
          type="button"
          className={classNames(
            'w-full flex items-center justify-between py-2 px-3 text-navy-500',
            {
              'rounded-md': !isNavBarOpen,
              'rounded-tr-md rounded-tl-md border-b-0': isNavBarOpen,
            }
          )}
          onClick={() => {
            setIsNavBarOpen(!isNavBarOpen);
          }}
        >
          <span>{navTitle}</span>
          <Icons.Arrow
            customClass={classNames('rotate-90', {
              '!rotate-[270deg]': isNavBarOpen,
            })}
          />
        </button>
        {isNavBarOpen && (
          <div className="absolute w-full bg-white border border-navy-200 cursor-pointer rounded-br-md rounded-bl-md">
            {filteredContent.map(({ navigationTitle, id }, ind) => {
              const activeStyles =
                activeItem.id === id && 'bg-navy-50 font-semibold';

              const lastItemStyles =
                filteredContent.length === ind + 1 &&
                'rounded-br-md rounded-bl-md';

              return (
                <button
                  type="button"
                  key={uniqid()}
                  className={classNames(
                    'w-full text-left py-2 px-3 cursor-pointer',
                    activeStyles,
                    lastItemStyles
                  )}
                  onClick={() => handleItemClick({ navigationTitle, id })}
                >
                  {navigationTitle}
                </button>
              );
            })}
          </div>
        )}
      </div>
    ))
  );
};

export default PageNav;
